import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Icon from '@mdi/react';
import { mdiMagnify, mdiEarth, mdiCheck } from '@mdi/js';
import useEscapeKey from '../hooks/useEscapeKey';
import { zoneOptionLabel } from '../utils/timezoneLabel';
import './TimezonePicker.css';

/**
 * TimezonePicker — inline portal dropdown for choosing an event's IANA timezone.
 * Emulates SingleMonthDatePicker: renders through a portal to document.body,
 * positioned from the trigger's bounding rect, closes on select + Escape +
 * outside-click. Stores the raw IANA string (e.g. "Pacific/Auckland").
 *
 * Props:
 *   isOpen       — controlled open state (lifted into the parent form)
 *   position     — { top, left } from the trigger's getBoundingClientRect
 *   value        — currently selected IANA string (or empty)
 *   defaultZone  — the org's business-location tz, shown first as the default
 *   onSelect(z)  — called with the chosen IANA string
 *   onClose()    — close the dropdown
 */

// Curated common zones shown first (AU/NZ + regional + global majors). De-duped
// with the org default + current value so the everyday choices are one glance away.
const COMMON_ZONES = [
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
    'Australia/Perth', 'Australia/Adelaide', 'Australia/Darwin', 'Australia/Hobart',
    'Pacific/Auckland', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Hong_Kong',
    'Asia/Kuala_Lumpur', 'Asia/Jakarta', 'Asia/Manila', 'Asia/Bangkok',
    'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles',
];

// Bundled fallback for browsers without Intl.supportedValuesOf (guards the risk
// in the PRP risk table — the common list still works without it).
const FALLBACK_ALL_ZONES = [
    ...COMMON_ZONES, 'UTC', 'Asia/Dubai', 'Asia/Kolkata', 'Europe/Berlin',
    'Europe/Madrid', 'Europe/Rome', 'America/Chicago', 'America/Denver',
    'America/Sao_Paulo', 'Pacific/Fiji', 'Pacific/Honolulu', 'Africa/Johannesburg',
];

function getAllZones() {
    try {
        if (typeof Intl.supportedValuesOf === 'function') {
            return Intl.supportedValuesOf('timeZone');
        }
    } catch (e) {
        // fall through to the bundled list
    }
    return FALLBACK_ALL_ZONES;
}

function TimezonePicker({ isOpen, position, value, defaultZone, onSelect, onClose }) {
    const [search, setSearch] = useState('');
    const [showAll, setShowAll] = useState(false);

    useEscapeKey(isOpen, onClose);

    // Close on an outside mousedown — matches SingleMonthDatePicker's closest() check.
    useEffect(() => {
        if (!isOpen) return undefined;
        const handleClickOutside = (e) => {
            if (!e.target.closest('.timezone-picker')) onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);

    // Reset transient UI each time it opens.
    useEffect(() => {
        if (isOpen) {
            setSearch('');
            setShowAll(false);
        }
    }, [isOpen]);

    const commonList = useMemo(() => {
        const seed = [defaultZone, value, ...COMMON_ZONES].filter(Boolean);
        return [...new Set(seed)];
    }, [defaultZone, value]);

    const allZones = useMemo(() => getAllZones(), []);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const source = (showAll || q) ? allZones : commonList;
        const list = q
            ? source.filter((z) => z.toLowerCase().includes(q) || zoneOptionLabel(z).toLowerCase().includes(q))
            : source;
        return [...new Set(list)];
    }, [search, showAll, allZones, commonList]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="timezone-picker" style={{ top: `${position.top}px`, left: `${position.left}px` }}>
            <div className="timezone-picker-search">
                <Icon path={mdiMagnify} size={0.8} className="timezone-picker-search-icon" />
                <input
                    type="text"
                    className="timezone-picker-search-input"
                    placeholder="Search timezones"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                />
            </div>
            <div className="timezone-picker-list">
                {rows.length === 0 && (
                    <div className="timezone-picker-empty">No timezones match your search</div>
                )}
                {rows.map((z) => (
                    <button
                        key={z}
                        type="button"
                        className={`timezone-picker-row${z === value ? ' is-selected' : ''}`}
                        onClick={() => {
                            onSelect(z);
                            onClose();
                        }}
                    >
                        <Icon path={mdiEarth} size={0.7} className="timezone-picker-row-icon" />
                        <span className="timezone-picker-row-label">{zoneOptionLabel(z)}</span>
                        {z === defaultZone && z !== value && (
                            <span className="timezone-picker-row-default">Default</span>
                        )}
                        {z === value && <Icon path={mdiCheck} size={0.7} className="timezone-picker-row-check" />}
                    </button>
                ))}
            </div>
            {!showAll && !search.trim() && (
                <button type="button" className="timezone-picker-seemore" onClick={() => setShowAll(true)}>
                    See all timezones
                </button>
            )}
        </div>,
        document.body
    );
}

export default TimezonePicker;
